import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Linkedin, MapPin, Building2 } from "lucide-react";

// Import images
import earthImage from "../images/earth.png";
import linkedinIcon from "../images/linkedin.png";

// Company logos
import metaLogo from "../images/logos/meta.png";
import microsoftLogo from "../images/logos/microsoft.png";
import amazonLogo from "../images/logos/amazon.png";
import awsLogo from "../images/logos/aws.png";
import lexmarkLogo from "../images/logos/lexmark.png";
import relianceJioLogo from "../images/logos/reliancejio.png";
import quantiphiLogo from "../images/logos/quantiphi.png";
import morganStanleyLogo from "../images/logos/morganstanley.png";
import deutscheBankLogo from "../images/logos/deutschebank.png";
import barclaysLogo from "../images/logos/barclays.png";
import codeacademyLogo from "../images/logos/codeacademy.png";

// Intern images
import Tejas from "../images/InternImages/Tejas.png";
import Neil from "../images/InternImages/Neil.png";
import tanvi from "../images/InternImages/tanvi.png";
import Sarah from "../images/InternImages/Sarah.png";
import Aditya from "../images/InternImages/Aditya.png";
import Sumit from "../images/InternImages/Sumit.png";
import Rithvika from "../images/InternImages/Rithvika.png";
import parth from "../images/InternImages/parth.png";
import Mahesh from "../images/InternImages/Mahesh.png";
import palak from "../images/InternImages/palak.png";
import SarahSonje from "../images/InternImages/SarahSonje.png";
import Smruti from "../images/InternImages/Smruti.png";
import Urvi from "../images/InternImages/Urvi.png";
import Aumkar from "../images/InternImages/Aumkar.png";
import Anjali from "../images/InternImages/Anjali.png";
import Tanisha from "../images/InternImages/Tanisha.png";

const COLORS = {
  bgDark: "#0a0f1c",
  primary: "#48bb78",
  primaryDark: "#074fb4",
  secondary: "#38b2ac",
  cardBg: "#131a3b",          // solid color
  cardBorder: "#adaeb1",      // solid border (or darker if too bright)
  textPrimary: "#ffffff",
  textSecondary: "rgba(255, 255, 255, 0.7)",
  textMuted: "rgba(255, 255, 255, 0.5)",
  mutedBg: "rgba(255, 255, 255, 0.05)",
  borderLight: "rgba(255, 255, 255, 0.1)",
};

const styles = {
  container: {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
    backgroundColor: COLORS.bgDark,
    padding: "64px 0",
  },
  backgroundGrid: {
    position: "absolute",
    inset: 0,
    opacity: 0.03,
    backgroundImage: `
      linear-gradient(${COLORS.primary}80 1px, transparent 1px),
      linear-gradient(90deg, ${COLORS.primary}80 1px, transparent 1px)
    `,
    backgroundSize: "60px 60px",
  },
  earthContainer: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  earthWrapper: {
    position: "relative",
    width: "800px",
    height: "800px",
  },
  earthImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    opacity: 0.2,
  },
  earthGlow: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${COLORS.primary}1a, ${COLORS.secondary}1a, transparent)`,
    filter: "blur(48px)",
  },
  orbitalRing: (size, borderColor) => ({
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    border: `1px solid ${borderColor}`,
  }),
  content: {
    position: "relative",
    zIndex: 10,
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "0 16px",
  },
  headerContainer: {
    textAlign: "center",
    marginBottom: "96px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    borderRadius: "9999px",
    backgroundColor: COLORS.mutedBg,
    border: `1px solid ${COLORS.borderLight}`,
    marginBottom: "24px",
  },
  badgeText: {
    fontSize: "14px",
    fontWeight: 500,
    color: COLORS.textMuted,
  },
  title: {
    fontSize: "clamp(2rem, 5vw, 4.5rem)",
    fontWeight: 700,
    marginBottom: "16px",
    lineHeight: 1.1,
  },
  titleWhite: {
    color: COLORS.textPrimary,
  },
  titleGradient: {
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  subtitle: {
    fontSize: "18px",
    color: COLORS.textSecondary,
    maxWidth: "640px",
    margin: "0 auto",
  },
  underline: {
    marginTop: "24px",
    marginLeft: "auto",
    marginRight: "auto",
    height: "4px",
    borderRadius: "9999px",
    background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`,
  },
  carouselContainer: {
    position: "relative",
    height: "550px",
    perspective: "1000px",
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 30,
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    cursor: "pointer",
    boxShadow: `0 4px 20px ${COLORS.primary}40`,
    color: COLORS.textPrimary,
  },
  cardsContainer: {
    position: "relative",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transformStyle: "preserve-3d",
  },
  indicators: {
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "8px",
  },
  indicator: (isActive) => ({
    height: "8px",
    width: isActive ? "32px" : "8px",
    borderRadius: "9999px",
    background: isActive
      ? `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`
      : `${COLORS.textMuted}4d`,
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    padding: 0,
  }),
  legacySection: {
    marginTop: "96px",
    textAlign: "center",
  },
  dividerContainer: {
    position: "relative",
    marginBottom: "32px",
  },
  dividerLine: {
    height: "1px",
    background: `linear-gradient(to right, transparent, ${COLORS.borderLight}, transparent)`,
  },
  dividerAccent: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "64px",
    height: "4px",
    borderRadius: "9999px",
    background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`,
  },
  legacyText: {
    fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
    color: COLORS.textPrimary,
    fontWeight: 500,
  },
  legacySubtext: {
    marginTop: "8px",
    color: COLORS.textSecondary,
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginTop: "24px",
  },
  // Card styles
  cardWrapper: {
    position: "absolute",
    transformStyle: "preserve-3d",
  },
  cardOuter: (isCenter) => ({
    position: "relative",
    width: "288px",
    transition: "all 0.5s ease",
    cursor: isCenter ? "pointer" : "default",
  }),
  cardGlow: {
    position: "absolute",
    inset: "-8px",
    borderRadius: "24px",
    background: `linear-gradient(to right, ${COLORS.primary}4d, ${COLORS.secondary}4d)`,
    filter: "blur(16px)",
  },
  card: {
    position: "relative",
    backgroundColor: COLORS.cardBg,
    borderRadius: "16px",
    border: `1px solid ${COLORS.cardBorder}`,
    overflow: "hidden",
    backdropFilter: "blur(12px)",
  },
  companyBanner: {
    position: "relative",
    height: "64px",
    background: `linear-gradient(to right, ${COLORS.primaryDark}, ${COLORS.primaryDark}80)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  companyLogoImg: {
    height: "40px",
    objectFit: "contain",
    position: "relative",
    zIndex: 10,
    opacity: 0.8,
  },
  photoSection: {
    position: "relative",
    padding: "16px 16px 0",
  },
  photoContainer: {
    position: "relative",
    width: "160px",
    height: "160px",
    margin: "0 auto",
  },
  photoRing: {
    position: "absolute",
    inset: "-4px",
    borderRadius: "50%",
    background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`,
  },
  photo: {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    overflow: "hidden",
    border: `4px solid ${COLORS.cardBg}`,
  },
  photoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoShine: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to top right, transparent, rgba(255,255,255,0.1), transparent)",
  },
  infoSection: {
    padding: "12px 16px 16px",
    textAlign: "center",
  },
  internName: {
    fontSize: "18px",
    fontWeight: 700,
    color: COLORS.textPrimary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  companyRow: {
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  companyName: {
    fontSize: "14px",
    fontWeight: 600,
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  roleName: {
    marginTop: "4px",
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.8)",
  },
  previousRole: {
    marginTop: "12px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 12px",
    borderRadius: "9999px",
    backgroundColor: COLORS.mutedBg,
    border: `1px solid ${COLORS.cardBorder}`,
    fontSize: "12px",
    color: "rgba(255, 255, 255, 0.8)"
  },
  linkedinButton: {
    marginTop: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "100%",
    padding: "10px",
    borderRadius: "12px",
    background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`,
    color: COLORS.textPrimary,
    fontWeight: 500,
    fontSize: "14px",
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all 0.2s ease",
  },
  cornerAccentTR: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "32px",
    height: "32px",
    borderTop: `2px solid ${COLORS.primary}4d`,
    borderRight: `2px solid ${COLORS.primary}4d`,
    borderTopRightRadius: "16px",
  },
  cornerAccentBL: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "32px",
    height: "32px",
    borderBottom: `2px solid ${COLORS.secondary}4d`,
    borderLeft: `2px solid ${COLORS.secondary}4d`,
    borderBottomLeftRadius: "16px",
  },
};

const InternCard = ({ intern, isCenter, isHovered }) => {
  return (
    <div style={styles.cardOuter(isCenter)}>
      {/* Card Glow */}
      <motion.div
        style={styles.cardGlow}
        animate={{
          opacity: isCenter ? 0.6 : 0.2,
          scale: isHovered && isCenter ? 1.05 : 1,
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Main Card */}
      <div style={styles.card}>
        {/* Company Logo Banner */}
        <div style={styles.companyBanner}>
          {/* Shine Effect */}
          <motion.div
            style={{
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "50%",
              height: "100%",
              background: "linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)",
              transform: "skewX(-15deg)",
            }}
            animate={{ x: ["-100%", "400%"] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatDelay: 2,
              ease: "easeInOut",
            }}
          />
          <img
            src={intern.companyLogo}
            alt={intern.company}
            style={{ ...styles.companyLogoImg, filter: intern.company === "Amazon" || intern.company === "AWS" ? "brightness(0) invert(1)" : "none" }}
          />
        </div>

        {/* Photo Section */}
        <div style={styles.photoSection}>
          <div style={styles.photoContainer}>
            {/* Photo Ring */}
            <motion.div
              style={styles.photoRing}
              animate={{ rotate: isCenter ? 360 : 0 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            />
            {/* Photo */}
            <div style={styles.photo}>
              <img
                src={intern.imageSrc}
                alt={intern.name}
                style={styles.photoImg}
              />
              <div style={styles.photoShine} />
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div style={styles.infoSection}>
          <h3 style={styles.internName}>{intern.name}</h3>

          <div style={styles.companyRow}>
            <MapPin size={14} color={COLORS.primary} />
            <span style={styles.companyName}>{intern.company}</span>
          </div>

          <p style={styles.roleName}>{intern.role}</p>

          <div style={styles.previousRole}>
            <span>Previously: {intern.previousRole}</span>
          </div>

          {/* LinkedIn Button */}
          <motion.a
            href={intern.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkedinButton}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Linkedin size={16} />
            Connect on LinkedIn
          </motion.a>
        </div>

        {/* Corner Accents */}
        <div style={styles.cornerAccentTR} />
        <div style={styles.cornerAccentBL} />
      </div>
    </div>
  );
};

const GlobalNetwork = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);

  const data = [
    {
      srNo: 0,
      name: "Tejas Chheda",
      previousRole: "Summer Research Intern",
      role: "Software Engineer - ML",
      company: "Meta",
      imageSrc: Tejas,
      linkedin: "https://www.linkedin.com/in/tejas-chh",
      companyLogo: metaLogo, // existing
    },
    {
      srNo: 1,
      name: "Neil Daftary",
      previousRole: "Backend Development Intern",
      role: "Engineering Manager",
      company: "Codecademy",
      imageSrc: Neil,
      linkedin: "https://www.linkedin.com/in/neil-daftary",
      companyLogo: codeacademyLogo, // PNG
    },
    {
      srNo: 2,
      name: "Tanvi Dhope",
      previousRole: "Cloud Computing Intern",
      role: "Software Engineer 2",
      company: "Microsoft",
      imageSrc: tanvi,
      linkedin: "https://www.linkedin.com/in/tanvi-dhope",
      companyLogo: microsoftLogo, // existing
    },
    {
      srNo: 3,
      name: "Sarah Gawade",
      previousRole: "Frontend Development Intern",
      role: "Software Engineer",
      company: "Microsoft",
      imageSrc: Sarah,
      linkedin: "https://www.linkedin.com/in/sarah-gawde-7aa155176",
      companyLogo: microsoftLogo, // existing
    },
    {
      srNo: 4,
      name: "Aditya Das",
      previousRole: "Security Research Intern",
      role: "Senior Software Engineer",
      company: "Lexmark",
      imageSrc: Aditya,
      linkedin: "https://www.linkedin.com/in/adityadas96",
      companyLogo: lexmarkLogo,
    },
    {
      srNo: 5,
      name: "Sumit Gouthaman",
      previousRole: "Systems Engineering Intern",
      role: "Senior Software Engineer",
      company: "AWS",
      imageSrc: Sumit,
      linkedin: "https://www.linkedin.com/in/sumitgouthaman",
      companyLogo: awsLogo, //  existing
    },
    {
      srNo: 6,
      name: "Rithvika Iyer",
      previousRole: "Data Science Intern",
      role: "Machine Learning Engineer",
      company: "Reliance Jio",
      imageSrc: Rithvika,
      linkedin: "https://www.linkedin.com/in/rithvika-iyer-b93513154",
      companyLogo: relianceJioLogo,
    },
    {
      srNo: 7,
      name: "Parth Dedhia",
      previousRole: "Computer Vision Intern",
      role: "Senior ML Engineer",
      company: "Quantiphi",
      imageSrc: parth,
      linkedin: "https://www.linkedin.com/in/parth-dedhia",
      companyLogo: quantiphiLogo,
    },
    {
      srNo: 8,
      name: "Mahesh Tamse",
      previousRole: "Mobile Development Intern",
      role: "SDE 2",
      company: "Amazon Devices",
      imageSrc: Mahesh,
      linkedin: "https://www.linkedin.com/in/mahesh-tamse",
      companyLogo: amazonLogo, // same Amazon logo
    },
    {
      srNo: 9,
      name: "Palak Davda",
      previousRole: "Backend Services Intern",
      role: "SDE",
      company: "Amazon",
      imageSrc: palak,
      linkedin: "https://www.linkedin.com/in/palak-davda",
      companyLogo: amazonLogo, // existing
    },
    {
      srNo: 10,
      name: "Sarah Sonje",
      previousRole: "Distributed Systems Intern",
      role: "SDE",
      company: "Amazon",
      imageSrc: SarahSonje,
      linkedin: "https://www.linkedin.com/in/sarah-sonje",
      companyLogo: amazonLogo, // existing
    },
    {
      srNo: 11,
      name: "Smruti Varvadekar",
      previousRole: "IoT Development Intern",
      role: "SDE",
      company: "AWS IoT",
      imageSrc: Smruti,
      linkedin: "https://www.linkedin.com/in/smruti-varvadekar-558294122",
      companyLogo: awsLogo, // reuse AWS logo
    },
    {
      srNo: 12,
      name: "Urvi Bhanushali",
      previousRole: "FinTech Development Intern",
      role: "Senior Tech Associate",
      company: "Morgan Stanley",
      imageSrc: Urvi,
      linkedin: "https://www.linkedin.com/in/urvibh",
      companyLogo: morganStanleyLogo,
    },
    {
      srNo: 13,
      name: "Aumkar Gadekar",
      previousRole: "Data Analytics Intern",
      role: "Senior Analyst",
      company: "Deutsche Bank",
      imageSrc: Aumkar,
      linkedin: "https://www.linkedin.com/in/aumkar-gadekar",
      companyLogo: deutscheBankLogo,
    },
    {
      srNo: 14,
      name: "Anjali Karvinde",
      previousRole: "Technology Solutions Intern",
      role: "Technology Analyst",
      company: "Deutsche Bank",
      imageSrc: Anjali,
      linkedin: "https://www.linkedin.com/in/anjali-kanvinde",
      companyLogo: deutscheBankLogo,
    },
    {
      srNo: 15,
      name: "Tanisha Kulkarni",
      previousRole: "Business Intelligence Intern",
      role: "Business Analyst",
      company: "Barclays",
      imageSrc: Tanisha,
      linkedin: "https://www.linkedin.com/in/tanisha-kulkarni",
      companyLogo: barclaysLogo,
    },
  ];

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
    setIsAutoPlaying(false);
  }, [data.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % data.length);
    setIsAutoPlaying(false);
  }, [data.length]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % data.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, data.length]);

  const getVisibleCards = () => {
    const cards = [];
    const visibleCount = 5;
    const halfVisible = Math.floor(visibleCount / 2);

    for (let i = -halfVisible; i <= halfVisible; i++) {
      const index = (currentIndex + i + data.length) % data.length;
      cards.push({
        intern: data[index],
        position: i,
        index,
      });
    }
    return cards;
  };

  const getCardStyle = (position) => {
    const absPosition = Math.abs(position);
    const isCenter = position === 0;

    return {
      zIndex: 10 - absPosition,
      x: position * 180,
      y: isCenter ? -20 : absPosition * 10,
      scale: isCenter ? 1.15 : 1 - absPosition * 0.12,
      rotateY: position * -15,
      opacity: 1 - absPosition * 0.25,
    };
  };

  return (
    <div style={styles.container}>
      {/* Background Grid */}
      <div style={styles.backgroundGrid} />

      {/* Floating Particles */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            backgroundColor: `${COLORS.primary}4d`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}

      {/* Earth Background */}
      <div style={styles.earthContainer}>
        <motion.div
          style={styles.earthWrapper}
          animate={{ rotate: 360 }}
          transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
        >
          <img src={earthImage} alt="Earth" style={styles.earthImage} />
          <div style={styles.earthGlow} />
        </motion.div>
      </div>

      {/* Orbital Rings */}
      <div style={styles.earthContainer}>
        <motion.div
          style={styles.orbitalRing("700px", `${COLORS.primary}1a`)}
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          style={styles.orbitalRing("850px", `${COLORS.secondary}1a`)}
          animate={{ rotate: -360 }}
          transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Header */}
        <motion.div
          style={styles.headerContainer}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            style={styles.badge}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Building2 size={16} color={COLORS.primary} />
            <span style={styles.badgeText}>Our Global Alumni Network</span>
          </motion.div>

          <h2 style={styles.title}>
            <span style={styles.titleWhite}>Our Alumni at </span>
            <br />
            <span style={styles.titleGradient}>Leading Companies</span>
          </h2>

          <p style={styles.subtitle}>
            Our alumni are making an impact at the world's leading technology companies
          </p>

          <motion.div
            style={styles.underline}
            initial={{ width: 0 }}
            animate={{ width: 120 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          />
        </motion.div>

        {/* 3D Carousel */}
        <div style={styles.carouselContainer}>
          {/* Nav Buttons */}
          <motion.button
            style={{ ...styles.navButton, left: "8px" }}
            onClick={handlePrev}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft size={24} />
          </motion.button>

          <motion.button
            style={{ ...styles.navButton, right: "8px" }}
            onClick={handleNext}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight size={24} />
          </motion.button>

          {/* Cards */}
          <div style={styles.cardsContainer}>
            <AnimatePresence mode="popLayout">
              {getVisibleCards().map(({ intern, position, index }) => (
                <motion.div
                  key={intern.srNo}
                  style={styles.cardWrapper}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={getCardStyle(position)}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <InternCard
                    intern={intern}
                    isCenter={position === 0}
                    isHovered={hoveredCard === index}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Indicators */}
          <div style={styles.indicators}>
            {data.map((_, index) => (
              <motion.button
                key={index}
                style={styles.indicator(currentIndex === index)}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsAutoPlaying(false);
                }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </div>

        {/* Legacy Section */}
        <motion.div
          style={styles.legacySection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
        </motion.div>
      </div>
    </div>
  );
};

export default GlobalNetwork;
